// Copyright © 2015-2020 TrampleZone LLC All Rights Reserved

// ModalWindow Module

// A ModalWindow is a top-level window which can only be dismissed with a
// provided close option.  It is meant to display specific content until
// actively dismissed.  Using the mask option, events on surrounding content
// can be disabled.  It is expected that CSS3 styling options are used and
// transitions and events are in effect.  Thus, optional callbacks are
// invoked only when transitionend events are fired.
//
// ModalWindow uses position absolute.  It will be positioned relative to the
// next parent that is position relative.  To better control position, it is
// recommended that a dedicated div with position relative is used as the
// parent of ModalWindow.

// Dependency: Utilities.addEventListeners()

// Public API
//
// # Constructor:
// ModalWindow(options)
// @param options {
//      component:     element to embed (required, must be a div element);
//      id:            css styling id for modal window element
//      bounds:        bounds {top,right,bottom,left} for modal window
//      originBounds:  bounds {top,right,bottom,left} for transition origin
//      useMask:       true|false to use a background mask, (default false)
// }
//
// # Open the modal window with optional callback upon completion
// ModalWindow.open([callback])
//
// # Close the modal window with optional callback upon completion
// ModalWindow.close([callback])
//
// # Set the modal window bounds, default is next parent using position: relative
// ModalWindow.setBounds(bounds)
//
// # Set the zoom position to the specified bounds, default is parent center
// ModalWindow.setOriginBounds(originBounds)
//
// # Update the use of a shaded mask for the modal window, true or false
// ModalWindow.setMask(useMask)
//
// # Get the modal window container div
// ModalWindow.getDiv()

define('ModalWindow',['require'],function (require) {
    'use strict';

    // Constructor
    function ModalWindow (modalWindowOptions) {

        // Initialize options map
        this.options = {};

        // Default Options
        this.defaultOptions = {
            component:      undefined,
            id:             undefined,
            bounds:         {top:0, right:0, bottom:0, left:0},
            originBounds:   {top:0, right:0, bottom:0, left:0},
            useMask:        false,
        };

        // Make sure the parameter is not null as component is required
        if (!modalWindowOptions) {
            throw 'Invalid ModalWindow options - ' + modalWindowOptions;
        }

        // Validate specified component (required) and set options
        if (modalWindowOptions.component &&
            modalWindowOptions.component.tagName.toLowerCase() == 'div') {
            this.options.component = modalWindowOptions.component;
        } else {
            throw 'Invalid ModalWindow component (div element) - ' +
                modalWindowOptions.component.tagName;
        }

        // Create container and component html elements and assemble
        this.closeButtonDiv = document.createElement('div');
        this.closeButtonSpan = document.createElement('span');
        this.closeButtonDiv.appendChild(this.closeButtonSpan);
        this.closeButtonSpan.innerHTML = '&times;';
        this.closeDiv = document.createElement('div');
        this.closeDiv.appendChild(this.closeButtonDiv);
        this.container = document.createElement('div');
        this.container.style.display = 'none';
        this.container.appendChild(this.closeDiv);
        this.container.appendChild(this.options.component);
        document.onkeyup = function (e) {
            if (e.keyCode == 27) {
                this.close();
            }
        }.bind(this);

        // Validate ID used for styling
        if (modalWindowOptions.id && modalWindowOptions.id.indexOf(' ') < 0) {
            this.setId(modalWindowOptions.id);
        } else {
            throw 'Invalid CSS styling id for modal window - ' +
                modalWindowOptions.id;
        }

        // Class names for dynamic update via transitions
        this.modalWindowClass = this.options.id + '-window';
        this.modalOpenClass = this.options.id + '-open';
        this.maskOpenClass = this.options.id + '-mask-open';

        // Apply mask
        this.setUseMask(modalWindowOptions.useMask || this.defaultOptions.useMask);

        // Set bounds
        this.setBounds(modalWindowOptions.bounds || this.defaultOptions.bounds);

        // Set origin bounds (for zooming, etc)
        this.setOriginBounds(
            modalWindowOptions.originBounds || this.defaultOptions.originBounds);

        // Get the transition event for this platform
        this.transitionEvent = this.getTransitionEvent();

        // Enable the close button
        this.closeDiv.onclick = function () {
            this.close();
        }.bind(this);
    };


    // PUBLIC METHODS

    // Open ModalWindow with optional callback when complete
    ModalWindow.prototype.open = function (callback) {
        if (this.isOpen()) {
            // Already open
            if (callback) {
                callback();
            }
        } else {
            // Set modal window class, if not present (support setBounds())
            if (!this.container.classList.contains(this.modalWindowClass)) {
                this.container.style.display = 'block';
                if (this.options.useMask) {
                    this.maskContainer.style.display = 'block';
                }
                this.reflow();
                this.container.classList.add(this.modalWindowClass);
            }
            // Optionally wait for transition end for callback
            if (callback) {
                this.addTransitionListener(this.container, callback);
            }
            // Full size div is now in place but invisible, minimize it
            this.container.classList.remove('close');
            this.container.classList.add('minimized');
            // Transition to full size
            setTimeout(function() {
                // Show mask if requested
                if (this.options.useMask) {
                    this.maskContainer.classList.add(this.maskOpenClass);
                }
                // Show the target container
                this.container.classList.add('open');
            }.bind(this), 150);
            this.container.classList.add(this.modalOpenClass);
        }
    };

    // Close ModalWindow with optional callback when complete
    ModalWindow.prototype.close = function (callback) {
        if (this.isOpen()) {
            // Close and optionally wait for transition end
            this.addTransitionListener(this.container, function () {
                // Set the window to closed state
                this.container.classList.remove(this.modalOpenClass);
                // Remove modal window class, if present (support setBounds())
                if (this.container.classList.contains(this.modalWindowClass)) {
                    this.container.classList.remove(this.modalWindowClass);
                    if (this.options.useMask) {
                        this.maskContainer.style.display = 'none';
                    }
                }
                if (callback) {
                    callback();
                }
            }.bind(this));
            this.container.classList.remove('open');
            this.container.classList.add('close');
            // Remove mask if present
            if (this.options.useMask) {
                this.maskContainer.classList.remove(this.maskOpenClass);
            }
        } else {
            // Already closed
            if (callback) {
                callback();
            }
        }
    };

    // Get the state of the control
    ModalWindow.prototype.isOpen = function () {
        return this.container.classList.contains(this.modalOpenClass);
    };

    // Set CSS id to container to properly style
    ModalWindow.prototype.setId = function (id) {
        // Confirm id is a valid css selector id
        if (id && id.indexOf(' ') < 0) {
            this.options.id = id;
            this.container.setAttribute('id', id);
        } else {
            console.log('WARN: Invalid CSS selector id - ' + id);
        }
    };

    // Set the bounds for this modal window
    ModalWindow.prototype.setBounds = function (bounds) {
        // Validate bounds parameter, if specified
        if (bounds &&
            typeof bounds.top != 'undefined' &&
            typeof bounds.right != 'undefined' &&
            typeof bounds.bottom != 'undefined' &&
            typeof bounds.left != 'undefined') {
            // Reset bounds for the modal window
            this.options.bounds = bounds;
            var container = this.container;
            if (this.options.useMask) {
                container = this.maskContainer;
            }
            container.style.top = bounds.top + 'px';
            container.style.right = bounds.right + 'px';
            container.style.bottom = bounds.bottom + 'px';
            container.style.left = bounds.left + 'px';
        } else {
            var warning = 'WARN: Invalid bounds specified - ';
            if (bounds) {
                warning += '(';
                warning += bounds.top + ',';
                warning += bounds.right + ',';
                warning += bounds.bottom + ',';
                warning += bounds.left;
                warning += ')';
            } else {
                warning += 'undefined';
            }
            console.log(warning);
        }
    };

    // Set the (zoom) origin bounds for this modal window
    ModalWindow.prototype.setOriginBounds = function (originBounds) {
        // Validate bounds parameter, if specified
        if (originBounds &&
            typeof originBounds.top != 'undefined' &&
            typeof originBounds.right != 'undefined' &&
            typeof originBounds.bottom != 'undefined' &&
            typeof originBounds.left != 'undefined') {
            // Reset bounds for the modal window origin
            this.options.originBounds = originBounds;
            this.container.style.top = originBounds.top + 'px';
            this.container.style.left = originBounds.left + 'px';
            this.container.style.width = originBounds.right - originBounds.left + 'px';
            this.container.style.height = originBounds.bottom - originBounds.top + 'px';
        } else {
            var warning = 'WARN: Invalid origin bounds specified - ';
            if (originBounds) {
                warning += '(';
                warning += originBounds.top + ',';
                warning += originBounds.right + ',';
                warning += originBounds.bottom + ',';
                warning += originBounds.left;
                warning += ')';
            } else {
                warning += 'undefined';
            }
            console.log(warning);
        }
    };

    // Set mask boolean to specify whether or not to shade the background
    ModalWindow.prototype.setUseMask = function (useMask) {
        // Set default
        var validUseMask = this.defaultOptions.mask;
        // Validate useMask, if specified
        if (useMask != null && typeof useMask !== 'undefined') {
            if (useMask == true) {
                // Setup mask
                validUseMask = true;
                this.maskContainer = document.createElement('div');
                this.maskContainer.setAttribute('id', this.options.id + '-mask');
                this.maskContainer.appendChild(this.container);
                this.maskContainer.style.display = 'none';
                this.maskContainer.onclick = function (event) {
                    event.preventDefault();
                };
                // Disable external scrolling on mobile while mask is open
                this.maskContainer.addEventListener('touchmove',
                                                    function (event) {
                    event.preventDefault();
                });
            } else if (useMask == false) {
                // No mask
                validUseMask = false;
            } else {
                console.log('WARN: Invalid useMask setting - ' + useMask);
            }
        }
        // Set a valid mask boolean for the modal window
        this.options.useMask = validUseMask;
    };

    // Intentionally attempt to cause a reflow (though some browsers are too smart)
    ModalWindow.prototype.reflow = function () {
        var height = getComputedStyle(this.getDiv()).height;
        this.getDiv().style.height = (height + 500) + 'px';
        this.getDiv().style.height = height + 'px';
    };

    // Get the close div
    ModalWindow.prototype.getCloseDiv = function () {
        return this.closeDiv;
    };

    // Get the container div
    ModalWindow.prototype.getDiv = function () {
        var container = this.container;
        if (this.options.useMask) {
            container = this.maskContainer;
        }
        return container;
    };


    // PRIVATE METHODS

    // Get transition event for this platform
    ModalWindow.prototype.getTransitionEvent = function () {
        var transitionEvent;
        var el = document.createElement('div');
        var transitions = {
            'WebkitTransition' :'webkitTransitionEnd',
            'MozTransition'    :'transitionend',
            'MSTransition'     :'msTransitionEnd',
            'OTransition'      :'oTransitionEnd',
            'transition'       :'transitionend'
        };
        for (var t in transitions) {
            if (el.style[t] !== undefined ) {
                transitionEvent = transitions[t];
                break;
            }
        }
        return transitionEvent;
    };

    // Add event handler for transition events
    ModalWindow.prototype.addTransitionListener = function (element, callback) {
        var transitionEvent = this.transitionEvent;
        var handler = function() {
            element.removeEventListener(transitionEvent, handler, true);
            callback();
        };
        // Now add our new event listener
        element.addEventListener(transitionEvent, handler, true);
    };

    // Return Constructor
    return ModalWindow.prototype.constructor;
});

